import { getCompatibleCodesForSystem } from 'elements/hardwareSelector/SearchToolbar'
import { useTranslate } from 'ra-core'
import { useMemo } from 'react'
import type { ComponentTypesV2 } from 'types/selectComponent'
import type {
  ConfigurableFilterOptionsType,
  FilterComponentNodeV2,
  FilterGenericPropsType,
  FilterValuesType,
  OptionGenericType,
} from '../typeV2'
import { parseStringifiedListFilterValue } from '../utils/utils'

export const SYSTEM_COMPATIBILITY_FILTER_NODE_KEY_V2 = 'battery_compatibility_v2'
export const CODES_FILTER_KEY = 'codes'
export const SYSTEM_COMPATIBILITY_FILTER_KEY = 'system_compatibility'
export const SYSTEM_COMPATIBILITY_INVERTER_IDS_KEY = 'system_inverter_ids'
export const SYSTEM_COMPATIBILITY_MODULE_IDS_KEY = 'system_module_ids'
export const SYSTEM_COMPATIBILITY_TEMPERATURE_MIN_KEY = 'temperature_min'
export const SYSTEM_COMPATIBILITY_TEMPERATURE_MAX_KEY = 'temperature_max'

const SYSTEM_COMPATIBILITY_OPTIONS: OptionGenericType<string>[] = [
  { id: 'default', title: 'All', value: '' },
  { id: 'yes', title: 'Yes', value: '1' },
]

export const createSystemCompatibilityFilterNode = <T,>({
  label = 'Compatible with system',
  rendererComponent: RendererComponent,
  compatibleCodes = [],
  ...restConfigs
}: ConfigurableFilterOptionsType<string, string> & {
  compatibleCodes?: string[]
}): FilterComponentNodeV2 => {
  const getSystemDesignFilters = () => {
    const inverterIds = window.editor.selectedSystem
      ?.inverters()
      .filter((i) => i.getIsOptimizable() && !!i.inverter_id) // Filter out ad-hoc or non-SE components
      .map((i) => i.inverter_id)
      .join(',')

    const moduleIds = window.editor.selectedSystem
      ?.getModuleTypes()
      .filter((m) => !!m.componentId) // Filter out ad-hoc components
      .map((m) => m.componentId)
      .join(',')

    const project = window.projectForm?.getState().values
    const min_max = project?.temperature_min_max
    const min = project?.temperature_min_override === null ? min_max?.[0] : project?.temperature_min_override
    const max = project?.temperature_max_override === null ? min_max?.[1] : project?.temperature_max_override

    return {
      [SYSTEM_COMPATIBILITY_INVERTER_IDS_KEY]: inverterIds,
      [SYSTEM_COMPATIBILITY_MODULE_IDS_KEY]: moduleIds,
      [SYSTEM_COMPATIBILITY_TEMPERATURE_MIN_KEY]: min,
      [SYSTEM_COMPATIBILITY_TEMPERATURE_MAX_KEY]: max,
    }
  }

  const hasOptimizableInverter = () =>
    !!window.editor.selectedSystem &&
    window.editor.selectedSystem?.inverters().some((inverter) => inverter.getIsOptimizable())

  // Only pre-select when the selector is opened specifically for DC optimizers (e.g. inverter tab,
  // SolarEdge CTA, panels tab). Do not pre-select when dc_optimizer is one of many other types
  // (e.g. Others tab "+ Other").
  const shouldPreselectDcOptimizerCompatibility = (componentTypes: Set<ComponentTypesV2>) => {
    return componentTypes.size === 1 && componentTypes.has('dc_optimizer') && hasOptimizableInverter()
  }

  const getDefaultFilterValues = ({ componentTypes }: { componentTypes: Set<ComponentTypesV2> }) => {
    if (shouldPreselectDcOptimizerCompatibility(componentTypes)) {
      return {
        [SYSTEM_COMPATIBILITY_FILTER_KEY]: '1',
        ...getSystemDesignFilters(),
      }
    }
  }

  const doShowDcOptimizerCompatibility = (componentTypes: Set<ComponentTypesV2>) => {
    return componentTypes.has('dc_optimizer') && hasOptimizableInverter()
  }

  const FilterComponent = ({
    allFilterValues,
    modifiableFilterValues,
    setModifiableFilters,
  }: FilterGenericPropsType) => {
    // Use provided compatibleCodes or fall back to system-based lookup
    const codes = useMemo(() => {
      if (compatibleCodes && compatibleCodes.length > 0) {
        return compatibleCodes
      }
      return getCompatibleCodesForSystem('battery')
    }, [compatibleCodes])

    const types = parseStringifiedListFilterValue<ComponentTypesV2>({
      filterValues: allFilterValues,
      key: 'types',
    })
    const translate = useTranslate()
    const batteryWithCompatibleCodes = types.has('battery') && codes.length !== 0

    // Should only be available in design area, not in shop, so check selectedSystem
    const dcOptimizerCompatibility = doShowDcOptimizerCompatibility(types)

    if (!batteryWithCompatibleCodes && !dcOptimizerCompatibility) {
      return null
    }

    const onSelect = (value) => {
      if (batteryWithCompatibleCodes) {
        if (value === '1') {
          setModifiableFilters({
            ...modifiableFilterValues,
            [CODES_FILTER_KEY]: codes.join(','),
          })
        } else {
          if (!!modifiableFilterValues[CODES_FILTER_KEY]) {
            const newModifiableFilterValues = { ...modifiableFilterValues }
            delete newModifiableFilterValues[CODES_FILTER_KEY]
            setModifiableFilters(newModifiableFilterValues)
          }
        }
      } else {
        setModifiableFilters({
          ...modifiableFilterValues,
          [SYSTEM_COMPATIBILITY_FILTER_KEY]: value,
          ...getSystemDesignFilters(),
        })
      }
    }

    const getBatteryCompatibilityValue = ({ filterValues }: { filterValues: FilterValuesType }) => {
      const filterCodes = filterValues[CODES_FILTER_KEY]
      const compatible = !!filterCodes && filterCodes === codes.join(',')
      return compatible ? '1' : ''
    }

    const value = batteryWithCompatibleCodes
      ? getBatteryCompatibilityValue({ filterValues: allFilterValues })
      : allFilterValues[SYSTEM_COMPATIBILITY_FILTER_KEY]

    return (
      <RendererComponent
        id={SYSTEM_COMPATIBILITY_FILTER_NODE_KEY_V2}
        defaultValue={''}
        label={translate(label)}
        options={SYSTEM_COMPATIBILITY_OPTIONS}
        disabled={false}
        onSelect={onSelect}
        value={value}
      />
    )
  }

  return {
    key: SYSTEM_COMPATIBILITY_FILTER_NODE_KEY_V2,
    component: FilterComponent,
    getDefaultFilterValues: getDefaultFilterValues,
  }
}

export default createSystemCompatibilityFilterNode
